import React, { useEffect, useState, useRef, useCallback } from "react";
import { RefreshCw, Users, AlertCircle, GitBranch } from "lucide-react";

const ROLE_MAP = {
  3: { label: "Agent", icon: "👤", color: "#3b82f6", bg: "#dbeafe" },
  4: { label: "Sub-Agent", icon: "💼", color: "#f59e0b", bg: "#fef3c7" },
  5: { label: "Student", icon: "🎓", color: "#10b981", bg: "#d1fae5" },
};

const ROLE_OPTIONS = [
  { id: 3, label: "Agents", icon: "👤" },
  { id: 4, label: "Sub-Agents", icon: "💼" },
  { id: 5, label: "Students", icon: "🎓" },
];

export default function UserRelationTree() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [zoom, setZoom] = useState(1);

  // Recursive Tree Node Component
  const TreeNode = ({ node, level = 0 }) => {
    const [expanded, setExpanded] = useState(true);
    const role = ROLE_MAP[node.role_id] || ROLE_MAP[5];
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div className="flex flex-col select-none">
        {/* Node */}
        <div
          className="flex items-center gap-3 p-4 rounded-lg border-2 shadow-md hover:shadow-lg hover:scale-105 transition-all cursor-pointer mb-2"
          style={{
            marginLeft: `${level * 40}px`,
            backgroundColor: role.bg,
            borderColor: role.color,
          }}
        >
          {hasChildren && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-lg font-bold text-gray-600 hover:text-gray-800 w-6 h-6 flex items-center justify-center hover:bg-white/30 rounded"
            >
              {expanded ? "−" : "+"}
            </button>
          )}
          {!hasChildren && <div className="w-6"></div>}

          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-xl flex-shrink-0">{role.icon}</span>
            <div className="min-w-0 flex-1">
              <h4 className="font-semibold text-sm text-gray-800 truncate">
                {node.first_name} {node.last_name}
              </h4>
              <p className="text-xs text-gray-600">{role.label}</p>
            </div>
          </div>

          {hasChildren && (
            <div className="text-xs font-semibold bg-blue-200 text-blue-700 px-2 py-1 rounded flex-shrink-0">
              {node.children.length}
            </div>
          )}
        </div>

        {/* Email */}
        <div
          className="text-xs text-gray-700 px-4 py-2 rounded border border-gray-200 bg-gray-50 mb-2 truncate"
          style={{
            marginLeft: `${level * 40}px`,
          }}
        >
          📧 {node.email}
        </div>

        {/* Children */}
        {expanded && hasChildren && (
          <div>
            {node.children.map((child, idx) => (
              <TreeNode key={idx} node={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Fetch data based on selected role
  const fetchData = useCallback(async () => {
    if (!selectedRole) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        role_filter: selectedRole,
      });

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user-tree?${params}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        setUsers(result.data || []);
        if (result.data && result.data.length > 0) {
          setSelectedUser(result.data[0]);
        } else {
          setSelectedUser(null);
        }
      } else {
        throw new Error(result.message || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching tree data:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-sm border border-gray-200">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-800 mb-2">
            Error Loading Tree
          </h2>
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-lg flex flex-col border-r border-gray-200">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <h2 className="text-xl font-bold">Organization Tree</h2>
          <p className="text-blue-100 text-sm mt-2">Select a role to explore</p>
        </div>

        {/* Role Selector */}
        <div className="p-5 space-y-3 border-b border-gray-200">
          {ROLE_OPTIONS.map((role) => {
            const isSelected = selectedRole === role.id;
            const roleData = ROLE_MAP[role.id];

            return (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all transform hover:scale-105 active:scale-100 ${
                  isSelected
                    ? `border-[${roleData.color}] bg-[${roleData.bg}] shadow-lg ring-2 ring-offset-2 ring-[${roleData.color}]`
                    : "border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow-md"
                }`}
                style={
                  isSelected
                    ? {
                        borderColor: roleData.color,
                        backgroundColor: roleData.bg,
                        boxShadow: `0 0 0 3px ${roleData.color}20`,
                      }
                    : {}
                }
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{role.icon}</span>
                  <div className="text-left">
                    <div className="font-semibold text-gray-800">
                      {role.label}
                    </div>
                    <div className="text-xs text-gray-600">
                      {isSelected ? "Selected" : "Click to view"}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* User List */}
        {selectedRole && (
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {users.length > 0 ? (
              users.map((user) => {
                const role = ROLE_MAP[user.role_id] || ROLE_MAP[5];
                const isSelected = selectedUser?.id === user.id;

                return (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all transform hover:scale-105 ${
                      isSelected
                        ? "border-blue-500 bg-blue-50 shadow-md"
                        : "border-gray-200 bg-gray-50 hover:border-blue-300 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{role.icon}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm text-gray-800 truncate">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-xs text-gray-500">{role.label}</div>
                      </div>
                      {user.children?.length > 0 && (
                        <div className="text-xs bg-blue-200 text-blue-700 px-2 py-1 rounded-lg font-semibold flex-shrink-0">
                          {user.children.length}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No {ROLE_MAP[selectedRole]?.label.toLowerCase()} found</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-white overflow-hidden">
        {/* Header */}
        {selectedUser && (
          <div className="bg-white shadow-sm border-b border-gray-200 px-8 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="animate-fadeIn">
                <h1 className="text-2xl font-bold text-gray-800">
                  {selectedUser.first_name} {selectedUser.last_name}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {ROLE_MAP[selectedUser.role_id]?.label} •{" "}
                  {selectedUser.direct_children_count || selectedUser.children?.length || 0} in network
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all transform hover:scale-110 active:scale-95"
                  title="Zoom Out"
                >
                  <span className="text-lg font-bold text-gray-600">−</span>
                </button>
                <span className="text-sm text-gray-600 w-12 text-center font-semibold">
                  {(zoom * 100).toFixed(0)}%
                </span>
                <button
                  onClick={() => setZoom((z) => Math.min(3, z + 0.2))}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all transform hover:scale-110 active:scale-95"
                  title="Zoom In"
                >
                  <span className="text-lg font-bold text-gray-600">+</span>
                </button>
                <div className="w-px h-6 bg-gray-200"></div>
                <button
                  onClick={fetchData}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all transform hover:scale-110 active:scale-95"
                  title="Refresh"
                >
                  <RefreshCw size={20} className="text-gray-600 hover:text-gray-800" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tree Viewer */}
        <div className="flex-1 overflow-hidden bg-gradient-to-b from-gray-50 to-white relative">
          {!selectedRole ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block p-6 bg-white rounded-3xl shadow-lg border border-gray-200 mb-6">
                  <GitBranch className="w-16 h-16 text-gray-300 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Select a Role to Start
                </h3>
                <p className="text-gray-600 text-base max-w-sm">
                  Click on Agents, Sub-Agents, or Students in the sidebar
                </p>
              </div>
            </div>
          ) : loading ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="inline-block p-6 bg-white rounded-3xl shadow-lg border border-gray-200 mb-6">
                  <GitBranch className="w-16 h-16 text-blue-400 mx-auto animate-spin" />
                </div>
                <p className="text-gray-600 font-medium">
                  Loading {ROLE_MAP[selectedRole]?.label.toLowerCase()} network...
                </p>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800">
                  No Users Found
                </h3>
                <p className="text-gray-600">
                  No {ROLE_MAP[selectedRole]?.label.toLowerCase()} available
                </p>
              </div>
            </div>
          ) : !selectedUser ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-800">
                  Select a User
                </h3>
                <p className="text-gray-600">
                  Click on a user from the list to view their network
                </p>
              </div>
            </div>
          ) : (
            <div className="w-full h-full relative bg-white overflow-auto p-8">
              <div
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "top center",
                  transition: "transform 0.2s ease-out",
                }}
              >
                <TreeNode node={selectedUser} level={0} />
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
